import {useCallback} from 'react';

import * as vfsClient from '../utils/vfsClient';

/**
 * Owns the vfs-service half of the "vfs vs git" file-state decision for a
 * vfs-backed project — additive, not a replacement for
 * indexedDbFileStorage's git-backed path (still real, see task #101: those
 * projects are served by mia-flow-be and keep using IndexedDB unchanged).
 *
 * Scope note (task #68, following #114's investigation): a full removal of
 * IndexedDB from Canvas.jsx's live-preview-update pipeline (39 call sites —
 * file editing, AI-context merging, incremental "changed files" sync) was
 * investigated and deliberately deferred — IndexedDB is currently the real
 * data model for that pipeline's "changed since last preview push" tracking
 * and there's no drop-in replacement primitive yet (vfsClient's
 * getWorkingDiff() is a different concept, git-commit diff text). Converting
 * that safely needs the same live-tested, one-piece-at-a-time discipline
 * already used elsewhere in this refactor, not a blind swap.
 *
 * This hook covers the one piece that's safely, narrowly fixable now:
 * RepositoryTree.jsx's cold-start tree build used to fetch from vfs-service,
 * write through to IndexedDB, then recursively re-call loadTree() to read
 * the tree back OUT of IndexedDB — a wasted extra round-trip for the tree
 * structure itself (the IndexedDB write still happens, since
 * RepositoryTree's file-content reads for Monaco still depend on it — that
 * part is unchanged, real, and out of scope here).
 */
export function useVfsFileState() {
  const fetchServiceFiles = useCallback(
    (projectId, serviceName) => vfsClient.fetchServiceFiles(projectId, serviceName),
    [],
  );

  return {fetchServiceFiles, buildTreeFromFileMap};
}

/**
 * Builds the same tree-item shape RepositoryTree.jsx's IndexedDB-backed
 * loadTree() produces, directly from a vfs-service file map — no IndexedDB
 * read required. Every fetched file is fresh-from-vfs by definition, so
 * isNew/modified/deleted are always false (those flags only mean something
 * once local edits diverge from vfs-service, which is IndexedDB's own
 * unchanged responsibility to track once a file has been opened/edited).
 */
export function buildTreeFromFileMap(filesByPath) {
  const treeItems = [];
  const folders = new Set();

  for (const filePath of Object.keys(filesByPath)) {
    const pathParts = filePath.split('/');

    for (let i = 1; i < pathParts.length; i += 1) {
      folders.add(pathParts.slice(0, i).join('/'));
    }

    const fileName = pathParts[pathParts.length - 1];
    const parentPath = pathParts.slice(0, -1).join('/');

    treeItems.push({
      path: filePath,
      name: fileName,
      type: 'blob',
      level: pathParts.length - 1,
      parent: parentPath || undefined,
      isNew: false,
      modified: false,
      deleted: false,
    });
  }

  for (const folderPath of folders) {
    const folderParts = folderPath.split('/');
    const folderName = folderParts[folderParts.length - 1];
    const folderLevel = folderParts.length - 1;
    const parentPath = folderParts.slice(0, -1).join('/');

    treeItems.push({
      path: folderPath,
      name: folderName,
      type: 'tree',
      level: folderLevel,
      parent: parentPath || undefined,
    });
  }

  // Identical hierarchical sort to RepositoryTree.jsx's loadTree() — folders
  // before files at each level, alphabetical otherwise, parent before child.
  treeItems.sort((a, b) => {
    const aPath = a.path.split('/');
    const bPath = b.path.split('/');
    const minLength = Math.min(aPath.length, bPath.length);

    for (let i = 0; i < minLength; i += 1) {
      if (aPath[i] !== bPath[i]) {
        const aIsFolder = i < aPath.length - 1 || a.type === 'tree';
        const bIsFolder = i < bPath.length - 1 || b.type === 'tree';

        if (aIsFolder && !bIsFolder) return -1;
        if (!aIsFolder && bIsFolder) return 1;

        return aPath[i].localeCompare(bPath[i], undefined, {numeric: true, sensitivity: 'base'});
      }
    }

    if (aPath.length !== bPath.length) {
      return aPath.length - bPath.length;
    }

    if (a.type !== b.type) {
      return a.type === 'tree' ? -1 : 1;
    }

    return 0;
  });

  return treeItems;
}

export default useVfsFileState;
